/**
 * Web Search Service - Performs real web searches and saves results locally
 *
 * Integrates Google Custom Search API with local file storage for persistence.
 * Results are saved for offline access and historical reference.
 */

import { googleSearchClient, type SearchResponse, type SearchResult } from './googleSearchClient';
import { fileStorageService, type StoredFile } from './fileStorageService';

// =============================================================================
// TYPES
// =============================================================================

export interface SavedSearchResult extends SearchResponse {
  id: string;
  savedAt: string;
  fileId: string; // Reference to stored file
}

export interface SearchHistoryEntry {
  id: string;
  query: string;
  resultCount: number;
  savedAt: string;
  fileId: string;
}

// =============================================================================
// SEARCH WITH LOCAL STORAGE
// =============================================================================

/**
 * Perform a web search and save results locally
 */
export async function searchAndSave(
  query: string,
  numResults: number = 5
): Promise<SavedSearchResult> {
  // Check if API is configured
  const isHealthy = await googleSearchClient.checkHealth();
  if (!isHealthy) {
    throw new Error('Google Search API not configured. Please add VITE_GOOGLE_SEARCH_API_KEY and VITE_GOOGLE_SEARCH_ENGINE_ID to .env.local');
  }

  // Perform the search
  const searchResponse = await googleSearchClient.search(query, numResults);

  // Save results to local storage
  const storedFile = await fileStorageService.saveSearchResults(query, searchResponse.results);

  const savedResult: SavedSearchResult = {
    ...searchResponse,
    id: storedFile.id,
    savedAt: storedFile.createdAt,
    fileId: storedFile.id,
  };

  console.log(`[WebSearch] Saved search "${query}" with ${searchResponse.results.length} results (file: ${storedFile.id})`);

  return savedResult;
}

/**
 * Get a previously saved search by its file ID
 */
export async function getSavedSearch(fileId: string): Promise<SavedSearchResult | null> {
  const file = await fileStorageService.getFile(fileId);
  if (!file || file.type !== 'application/json') {
    return null;
  }

  try {
    const text = await file.data.text();
    const data = JSON.parse(text);

    return {
      id: file.id,
      query: data.query,
      results: data.results,
      totalResults: data.results.length,
      searchTime: 0, // Not available for saved results
      savedAt: data.timestamp || file.createdAt,
      fileId: file.id,
    };
  } catch (error) {
    console.error('[WebSearch] Failed to parse saved search:', error);
    return null;
  }
}

/**
 * Get search history (list of saved searches)
 */
export async function getSearchHistory(limit: number = 20): Promise<SearchHistoryEntry[]> {
  const files = await fileStorageService.listFiles({
    type: 'application/json',
    limit,
  });

  const history: SearchHistoryEntry[] = [];

  for (const fileRef of files) {
    // Only include search result files (they start with 'search-')
    if (!fileRef.id.startsWith('search-')) {
      continue;
    }

    try {
      const file = await fileStorageService.getFile(fileRef.id);
      if (file) {
        const text = await file.data.text();
        const data = JSON.parse(text);

        history.push({
          id: file.id,
          query: data.query,
          resultCount: data.results?.length || 0,
          savedAt: data.timestamp || file.createdAt,
          fileId: file.id,
        });
      }
    } catch {
      // Skip corrupted files
    }
  }

  return history;
}

/**
 * Delete a saved search
 */
export async function deleteSavedSearch(fileId: string): Promise<void> {
  await fileStorageService.deleteFile(fileId);
}

/**
 * Check if web search is available
 */
export async function isSearchAvailable(): Promise<boolean> {
  return googleSearchClient.checkHealth();
}

// =============================================================================
// SEARCH RESULT FORMATTING (for LLM context)
// =============================================================================

/**
 * Format search results as context for LLM
 */
export function formatResultsForLLM(results: SearchResult[]): string {
  if (!results || results.length === 0) {
    return 'Nessun risultato trovato.';
  }

  return results.map((r, i) => {
    return `[${i + 1}] ${r.title}\n   ${r.link}\n   ${r.snippet}`;
  }).join('\n\n');
}

/**
 * Format search results as markdown
 */
export function formatResultsAsMarkdown(query: string, results: SearchResult[]): string {
  if (!results || results.length === 0) {
    return `Nessun risultato trovato per "${query}".`;
  }

  let md = `**Risultati della ricerca per "${query}":**\n\n`;

  results.forEach((r, i) => {
    md += `${i + 1}. **[${r.title}](${r.link})**\n`;
    md += `   *${r.displayLink}*\n`;
    md += `   ${r.snippet}\n\n`;
  });

  return md;
}

// =============================================================================
// EXPORT
// =============================================================================

export const webSearchService = {
  searchAndSave,
  getSavedSearch,
  getSearchHistory,
  deleteSavedSearch,
  isSearchAvailable,
  formatResultsForLLM,
  formatResultsAsMarkdown,
};

export default webSearchService;
